import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { ReportBrandingCard } from '../components/ReportBranding.js';
import { Button, Card, Loading, Notice, SelectField, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { LANGUAGES, setLanguage, toLanguage } from '../i18n.js';
import { PrivacyCards } from './Privacy.js';
import { ME_KEY, useMe } from '../session.js';

const BUSINESS_TYPES = ['b2c_shop', 'b2c_services', 'b2b', 'info_only'] as const;
const COMPANY_SIZES = ['solo', 'micro', 'small', 'medium'] as const;

function timeZones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone').filter((z) => z.startsWith('Europe/'));
  } catch {
    return ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich'];
  }
}

/** S12: profile, company, language, time zone and password. */
export function Account() {
  const { t } = useTranslation();
  const me = useMe();
  const client = useQueryClient();
  const [form, setForm] = useState({
    fullName: '',
    name: '',
    timezone: 'Europe/Berlin',
    locale: 'de',
    businessType: '',
    companySize: '',
  });
  useEffect(() => {
    if (!me.data) return;
    setForm({
      fullName: me.data.user.fullName ?? '',
      name: me.data.account.name,
      timezone: me.data.account.timezone,
      locale: me.data.user.locale ?? me.data.account.locale,
      businessType: me.data.account.businessType ?? '',
      companySize: me.data.account.companySize ?? '',
    });
  }, [me.data]);
  const save = useMutation({
    mutationFn: async () => {
      const locale = toLanguage(form.locale);
      await unwrap(api.PATCH('/me', { body: { fullName: form.fullName.trim() || null, locale } }));
      await unwrap(
        api.PATCH('/account', {
          body: {
            name: form.name.trim(),
            timezone: form.timezone,
            locale,
            businessType: (form.businessType || null) as (typeof BUSINESS_TYPES)[number] | null,
            companySize: (form.companySize || null) as (typeof COMPANY_SIZES)[number] | null,
          },
        }),
      );
      setLanguage(locale);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ME_KEY }),
  });

  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const change = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/me/change-password', {
          body: { currentPassword: passwords.current, newPassword: passwords.next },
        }),
      ),
    onSuccess: () => setPasswords({ current: '', next: '' }),
  });

  if (!me.data) return <Loading />;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const changeError = change.error instanceof ApiError ? errorMessage(change.error, t) : undefined;
  return (
    <>
      <PageTitle>{t('account.title')}</PageTitle>
      <div className="flex max-w-2xl flex-col gap-6">
        <Card>
          <h2 className="mb-4 text-xl font-semibold">{t('account.profile')}</h2>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <p>
              <span className="font-semibold">{t('auth.email')}:</span>{' '}
              <span className="break-all">{me.data.user.email}</span>
            </p>
            <TextField
              label={t('account.fullName')}
              autoComplete="name"
              optional
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
            <TextField
              label={t('account.companyName')}
              autoComplete="organization"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <SelectField
              label={t('account.language')}
              value={form.locale}
              onChange={(e) => setForm({ ...form, locale: e.target.value })}
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l} lang={l}>
                  {t(`lang.${l}`)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('account.timezone')}
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            >
              {timeZones().map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('account.businessType')}
              value={form.businessType}
              onChange={(e) => setForm({ ...form, businessType: e.target.value })}
            >
              <option value="">{t('account.businessTypes.none')}</option>
              {BUSINESS_TYPES.map((b) => (
                <option key={b} value={b}>
                  {t(`account.businessTypes.${b}`)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('account.companySize')}
              value={form.companySize}
              onChange={(e) => setForm({ ...form, companySize: e.target.value })}
            >
              <option value="">{t('account.companySizes.none')}</option>
              {COMPANY_SIZES.map((s) => (
                <option key={s} value={s}>
                  {t(`account.companySizes.${s}`)}
                </option>
              ))}
            </SelectField>
            {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
            {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
            <div>
              <Button type="submit" busy={save.isPending}>
                {t('app.save')}
              </Button>
            </div>
          </form>
        </Card>
        <Card>
          <h2 className="mb-4 text-xl font-semibold">{t('account.passwordTitle')}</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              change.mutate();
            }}
            className="flex flex-col gap-4"
          >
            <TextField
              label={t('account.currentPassword')}
              type="password"
              autoComplete="current-password"
              value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
              required
            />
            <TextField
              label={t('account.newPassword')}
              type="password"
              autoComplete="new-password"
              hint={t('auth.register.passwordHint')}
              value={passwords.next}
              onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
              error={changeError}
              required
            />
            {change.isSuccess ? (
              <Notice kind="success">{t('account.passwordChanged')}</Notice>
            ) : null}
            <div>
              <Button type="submit" busy={change.isPending}>
                {t('account.changePassword')}
              </Button>
            </div>
          </form>
        </Card>
        <ReportBrandingCard />
        <PrivacyCards />
      </div>
    </>
  );
}
